/**
 * MarketContext.js
 * =================
 * React Context to manage country/market selection across the entire app
 * All components can access the current market context
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Country/Market configurations
const MARKET_CONFIG = {
  US: {
    code: 'US',
    name: 'United States',
    flag: '🇺🇸',
    currency: 'USD',
    currencySymbol: '$',
    locale: 'en-US',
    
    // Indices
    indices: [
      { symbol: 'SPY', name: 'S&P 500', displayName: 'S&P 500' },
      { symbol: 'DIA', name: 'Dow Jones', displayName: 'Dow Jones' },
      { symbol: 'QQQ', name: 'NASDAQ 100', displayName: 'NASDAQ' },
      { symbol: 'IWM', name: 'Russell 2000', displayName: 'Russell 2000' },
      { symbol: 'VIX', name: 'Volatility Index', displayName: 'VIX' }
    ],
    
    // Exchanges
    exchanges: ['NYSE', 'NASDAQ', 'AMEX'],
    
    // Default stocks for various features
    defaultStock: 'AAPL',
    popularStocks: ['AAPL', 'MSFT', 'GOOGL', 'AMZN', 'NVDA', 'META', 'TSLA'],
    
    // Market hours
    marketHours: {
      open: '09:30',
      close: '16:00',
      timezone: 'America/New_York',
      displayTz: 'EST'
    },
    
    // API endpoints (relative paths - backend handles routing)
    endpoints: {
      quote: '/api/stocks/quote',
      options: '/api/options',
      indices: '/api/market/indices',
      news: '/api/news',
      earnings: '/api/earnings',
      screener: '/api/stocks/screener'
    },
    
    // Features available for this market
    features: {
      options: true,
      futures: true,
      crypto: true,
      etf: true,
      congressTrades: true,
      insiderTrading: true,
      fedIndicators: true,
      socialSentiment: true
    }
  },
  
  IN: {
    code: 'IN',
    name: 'India',
    flag: '🇮🇳',
    currency: 'INR',
    currencySymbol: '₹',
    locale: 'en-IN',
    
    // Indices
    indices: [
      { symbol: 'NIFTY', name: 'NIFTY 50', displayName: 'NIFTY 50' },
      { symbol: 'SENSEX', name: 'S&P BSE SENSEX', displayName: 'SENSEX' },
      { symbol: 'BANKNIFTY', name: 'NIFTY Bank', displayName: 'Bank Nifty' },
      { symbol: 'NIFTYIT', name: 'NIFTY IT', displayName: 'Nifty IT' },
      { symbol: 'INDIAVIX', name: 'India VIX', displayName: 'India VIX' }
    ],
    
    // Exchanges
    exchanges: ['NSE', 'BSE'],
    
    // Default stocks for various features
    defaultStock: 'RELIANCE',
    popularStocks: ['RELIANCE', 'TCS', 'HDFCBANK', 'INFY', 'ICICIBANK', 'HINDUNILVR', 'ITC'],
    
    // Market hours
    marketHours: {
      open: '09:15',
      close: '15:30',
      timezone: 'Asia/Kolkata',
      displayTz: 'IST'
    },
    
    // API endpoints for India
    endpoints: {
      quote: '/api/india/stocks/quote',
      options: '/api/india/options',
      indices: '/api/india/indices',
      news: '/api/india/news',
      earnings: '/api/india/earnings',
      screener: '/api/india/stocks/screener'
    },
    
    // Features available for India market
    features: {
      options: true,
      futures: true,
      crypto: false, // Crypto trading restricted in India
      etf: true,
      congressTrades: false, // US-specific
      insiderTrading: true,
      fedIndicators: false, // US-specific, but can show RBI data
      socialSentiment: true,
      // India-specific features
      fiiDii: true, // FII/DII data
      mutualFunds: true
    }
  }
};

// Create the context
const MarketContext = createContext(null);

// Provider component
export const MarketProvider = ({ children }) => {
  // Load saved preference or default to US
  const [selectedMarket, setSelectedMarket] = useState(() => {
    const saved = localStorage.getItem('investorq_market');
    return saved && MARKET_CONFIG[saved] ? saved : 'US';
  });

  // Current market configuration
  const marketConfig = MARKET_CONFIG[selectedMarket];

  // Change market and save preference
  const changeMarket = useCallback((marketCode) => {
    if (MARKET_CONFIG[marketCode]) {
      setSelectedMarket(marketCode);
      localStorage.setItem('investorq_market', marketCode);
    }
  }, []);

  // Format currency based on market
  const formatCurrency = useCallback((value, options = {}) => {
    if (value === null || value === undefined) return '-';
    
    const { compact = false, decimals = 2 } = options;
    
    try {
      const formatter = new Intl.NumberFormat(marketConfig.locale, {
        style: 'currency',
        currency: marketConfig.currency,
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
        notation: compact ? 'compact' : 'standard'
      });
      
      return formatter.format(value);
    } catch {
      return `${marketConfig.currencySymbol}${value.toFixed(decimals)}`;
    }
  }, [marketConfig]);

  // Format number based on market locale
  const formatNumber = useCallback((value, options = {}) => {
    if (value === null || value === undefined) return '-';
    
    const { compact = false, decimals = 2 } = options;
    
    try {
      const formatter = new Intl.NumberFormat(marketConfig.locale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
        notation: compact ? 'compact' : 'standard'
      });
      
      return formatter.format(value);
    } catch {
      return value.toFixed(decimals);
    }
  }, [marketConfig]);

  // Check if a feature is available for current market
  const isFeatureAvailable = useCallback((featureName) => {
    return marketConfig.features[featureName] ?? false;
  }, [marketConfig]);

  // Get API endpoint for current market
  const getEndpoint = useCallback((endpointName) => {
    return marketConfig.endpoints[endpointName] || null;
  }, [marketConfig]);

  // Check if market is currently open
  const isMarketOpen = useCallback(() => {
    const now = new Date();
    const options = { timeZone: marketConfig.marketHours.timezone };
    const timeStr = now.toLocaleTimeString('en-US', { ...options, hour12: false });
    const [hours, minutes] = timeStr.split(':').map(Number);
    const currentMinutes = hours * 60 + minutes;
    
    const [openH, openM] = marketConfig.marketHours.open.split(':').map(Number);
    const [closeH, closeM] = marketConfig.marketHours.close.split(':').map(Number);
    const openMinutes = openH * 60 + openM;
    const closeMinutes = closeH * 60 + closeM;
    
    // Check if it's a weekday (0 = Sunday, 6 = Saturday)
    const day = now.getDay();
    const isWeekday = day > 0 && day < 6;
    
    return isWeekday && currentMinutes >= openMinutes && currentMinutes < closeMinutes;
  }, [marketConfig]);

  // Context value
  const value = {
    // Current market state
    selectedMarket,
    marketConfig,
    
    // Actions
    changeMarket,
    
    // Utilities
    formatCurrency,
    formatNumber,
    isFeatureAvailable,
    getEndpoint,
    isMarketOpen,
    
    // Quick access to common config
    currency: marketConfig.currency,
    currencySymbol: marketConfig.currencySymbol,
    flag: marketConfig.flag,
    indices: marketConfig.indices,
    defaultStock: marketConfig.defaultStock,
    popularStocks: marketConfig.popularStocks,
    exchanges: marketConfig.exchanges
  };

  return (
    <MarketContext.Provider value={value}>
      {children}
    </MarketContext.Provider>
  );
};

// Custom hook to use market context
export const useMarket = () => {
  const context = useContext(MarketContext);
  if (!context) {
    throw new Error('useMarket must be used within a MarketProvider');
  }
  return context;
};

// Export config for direct access if needed
export { MARKET_CONFIG };

export default MarketContext;
